import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { Mail, Clock, Send, ChevronDown, LifeBuoy, Loader2, MessageCircle } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import TranslateButton from "../components/TranslateButton";
import { toast } from "sonner";

/**
 * Support page — Help & Support.
 * - Contact card (email + hours from GET /api/support/info)
 * - In-app chat with admin (polls GET /api/support/messages every 5s)
 * - FAQ accordion (starter, English)
 * Requires an authenticated non-admin (customer or supplier). Admins are
 * redirected to the admin dashboard's Support Inbox tab.
 */

const FAQ = [
  {
    q: "How do I request a service?",
    a: "Tap the green Request button in the bottom navigation, choose a category, confirm your pickup location, pick a payment method, and hit Request Now. The nearest approved drivers are pinged instantly and the first to accept becomes your driver.",
  },
  {
    q: "How is the price calculated?",
    a: "Fares combine a base fee, a per-kilometer rate for the selected country, and a minimum fare. You see the live fare preview before you confirm — no surprises at the end.",
  },
  {
    q: "What payment methods are supported?",
    a: "Cash on arrival, PayPal (which accepts Visa, Mastercard, Amex, Apple Pay and Google Pay as guest funding sources on supported devices), and in-app wallet balance.",
  },
  {
    q: "How do refunds work?",
    a: "If a driver never arrives or your service was not delivered, contact support via this chat with your order ID. Refunds are reviewed by admin within one business day and returned to the original payment method.",
  },
  {
    q: "Can I cancel a request?",
    a: "Yes — you can cancel from the dispatch screen while we're still looking for a driver, or from your Activity page before the driver has started the job. Cancellation policies vary by country and category.",
  },
  {
    q: "How do I rate my driver?",
    a: "After the trip is marked complete, a rating card appears on your Activity page. Ratings help us keep quality high — abusive or unsafe drivers are removed after review.",
  },
  {
    q: "How do I become a driver / supplier?",
    a: "Register with the Supplier role, fill in your phone, photo, and vehicle plate/model/color, then wait for admin approval. Approved suppliers receive dispatch notifications for their category near their location.",
  },
  {
    q: "Why haven't I been approved yet?",
    a: "Every supplier is personally vetted by our team. Please make sure your profile photo, phone, and vehicle details are correct. Approvals are typically completed within 24 hours.",
  },
];

export default function Support() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const nav = useNavigate();
  const [info, setInfo] = useState({ email: "support@drivon.com", hours: "24/7" });
  const [messages, setMessages] = useState([]);
  const [threadState, setThreadState] = useState({ state: "bot", agent_id: null });
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [openFaqIdx, setOpenFaqIdx] = useState(-1);
  const sinceRef = useRef(null);
  const scrollRef = useRef(null);

  const refreshThreadState = async () => {
    try {
      const { data } = await api.get("/support/thread");
      setThreadState(data);
    } catch (_) {}
  };

  // Redirect admin to admin dashboard support inbox
  useEffect(() => {
    if (user?.role === "admin") nav("/admin", { replace: true });
  }, [user, nav]);

  useEffect(() => {
    api.get("/support/info").then((r) => setInfo(r.data)).catch(() => {});
  }, []);

  const fetchMessages = async () => {
    if (!user) return;
    try {
      const params = sinceRef.current ? { since: sinceRef.current } : {};
      const { data } = await api.get("/support/messages", { params });
      if (data.length > 0) {
        setMessages((prev) => {
          const merged = [...prev, ...data];
          const seen = new Set();
          return merged.filter((m) => (seen.has(m.message_id) ? false : (seen.add(m.message_id), true)));
        });
        sinceRef.current = data[data.length - 1].created_at;
      }
    } catch (_) {}
  };

  useEffect(() => {
    if (!user || user.role === "admin") return;
    fetchMessages();
    refreshThreadState();
    const id = setInterval(() => { fetchMessages(); refreshThreadState(); }, 5000);
    const onVisible = () => { if (document.visibilityState === "visible") { fetchMessages(); refreshThreadState(); } };
    document.addEventListener("visibilitychange", onVisible);
    return () => { clearInterval(id); document.removeEventListener("visibilitychange", onVisible); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages.length]);

  const send = async (bodyOverride) => {
    const body = (bodyOverride || text).trim();
    if (!body || sending) return;
    setSending(true);
    try {
      const { data } = await api.post("/support/messages", { text: body });
      setMessages((prev) => [...prev, data]);
      sinceRef.current = data.created_at;
      if (!bodyOverride) setText("");
      // Bot may have inserted follow-up messages — refetch shortly.
      setTimeout(() => { fetchMessages(); refreshThreadState(); }, 400);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSending(false);
    }
  };

  const requestAgent = () => send("Please connect me to a human agent");

  return (
    <div className="max-w-[600px] md:max-w-[900px] mx-auto px-4 py-4" data-testid="support-page">
      <div className="flex items-center gap-2">
        <div className="w-9 h-9 rounded-2xl bg-brand-accent/15 text-brand-accent flex items-center justify-center border border-brand-accent/30">
          <LifeBuoy size={18} />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest font-black text-ink-textDim">// SUPPORT</div>
          <h1 className="text-3xl font-black leading-tight">{t("support.title", "Help & Support")}</h1>
        </div>
      </div>
      <p className="mt-2 text-sm text-ink-textDim max-w-[600px]">
        {t("support.intro", "We're here 24/7. Chat with our team below or email us and we'll get back within a few hours.")}
      </p>

      {/* Contact card */}
      <div className="mt-5 bg-ink-surface border border-ink-border rounded-2xl p-4 grid gap-3 sm:grid-cols-2" data-testid="support-contact-card">
        <a href={`mailto:${info.email}`} className="flex items-center gap-3 hover:bg-ink-surface2 rounded-xl p-2 -m-2 transition" data-testid="support-email-link">
          <div className="w-10 h-10 rounded-xl bg-ink-surface2 flex items-center justify-center"><Mail size={16} /></div>
          <div className="min-w-0">
            <div className="text-[10px] uppercase font-black text-ink-textDim">Email</div>
            <div className="font-bold text-sm truncate" data-testid="support-email">{info.email}</div>
          </div>
        </a>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-ink-surface2 flex items-center justify-center"><Clock size={16} /></div>
          <div>
            <div className="text-[10px] uppercase font-black text-ink-textDim">Hours</div>
            <div className="font-bold text-sm" data-testid="support-hours">{info.hours}</div>
          </div>
        </div>
      </div>

      {/* Chat with bot + admin */}
      {user ? (
        <div className="mt-5 bg-ink-surface border border-ink-border rounded-2xl overflow-hidden" data-testid="support-chat-card">
          <div className="px-4 py-3 border-b border-ink-border flex items-center gap-2 justify-between">
            <div className="flex items-center gap-2 min-w-0">
              <MessageCircle size={14} className="text-brand-accent" />
              <div className="text-xs font-black uppercase tracking-widest truncate">{t("support.chatTitle", "Chat with Support")}</div>
            </div>
            <SupportStateBadge state={threadState.state} agentName={threadState.agent_name} />
          </div>
          {/* Escalate CTA — only when we haven't already handed off to an agent */}
          {["bot", "verified"].includes(threadState.state) && (
            <div className="px-4 py-2 border-b border-ink-border bg-ink-surface2/50 flex items-center justify-between gap-2">
              <div className="text-[11px] text-ink-textDim">
                {t("support.escalateHint", "The bot handles common questions. Need a human?")}
              </div>
              <button
                onClick={requestAgent}
                className="bg-brand-accent text-black rounded-full px-3 py-1 text-[10px] font-black uppercase tracking-widest"
                data-testid="support-request-agent"
              >
                {t("support.talkToAgent", "Talk to an agent")}
              </button>
            </div>
          )}
          <div ref={scrollRef} className="max-h-[360px] overflow-y-auto p-3 space-y-2 bg-ink-bg" data-testid="support-chat-messages">
            {messages.length === 0 && (
              <div className="text-center text-xs text-ink-textDim py-8">
                {t("support.chatEmpty", "Send us a message — a team member will reply here.")}
              </div>
            )}
            {messages.map((m) => {
              const mine = m.sender_id === user.id;
              const isAi = m.sender_role === "ai";
              const isBot = m.sender_role === "bot";
              const isAdmin = m.sender_role === "admin";
              return (
                <div key={m.message_id} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`support-msg-${m.message_id}`}>
                  <div className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm ${
                    mine
                      ? "bg-brand-accent text-white rounded-br-md"
                      : isBot
                        ? "bg-electric-teal/10 text-white rounded-bl-md border border-electric-teal/40"
                        : isAi
                          ? "bg-brand-accent/10 text-white rounded-bl-md border border-brand-accent/30"
                          : isAdmin
                            ? "bg-amber-gold/10 text-white rounded-bl-md border border-amber-gold/40"
                            : "bg-ink-surface2 text-white rounded-bl-md border border-ink-border"
                  }`}>
                    {!mine && (
                      <div className="text-[10px] uppercase font-black text-ink-textDim mb-0.5 flex items-center gap-1">
                        {isBot && <span className="text-electric-teal">🤖</span>}
                        {isAi && <span className="text-brand-accent">✨</span>}
                        {isAdmin && <span className="text-amber-gold">👤</span>}
                        {isBot ? "Drivons Bot" : isAi ? "Drivons AI" : isAdmin ? (m.sender_name || "Support Agent") : m.sender_name}
                      </div>
                    )}
                    <div className="whitespace-pre-wrap break-words">{m.text}</div>
                    {!mine && !isBot && <TranslateButton messageId={m.message_id} text={m.text} />}
                  </div>
                </div>
              );
            })}
          </div>
          <div className="border-t border-ink-border p-2 flex items-center gap-2 bg-ink-surface">
            <input
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") send(); }}
              placeholder={t("support.placeholder", "Type your message…")}
              className="flex-1 bg-ink-bg border border-ink-border rounded-full px-4 py-2 text-sm outline-none focus:border-brand-accent"
              data-testid="support-chat-input"
            />
            <button
              onClick={send}
              disabled={sending || !text.trim()}
              className="w-9 h-9 rounded-full bg-brand-accent text-white flex items-center justify-center disabled:opacity-40"
              data-testid="support-chat-send"
            >
              {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
            </button>
          </div>
        </div>
      ) : (
        <div className="mt-5 bg-ink-surface border border-ink-border rounded-2xl p-4 text-sm text-ink-textDim" data-testid="support-signin-cta">
          <Link to="/login" className="text-brand-accent font-black underline">{t("nav.signIn", "Sign in")}</Link>{" "}
          {t("support.signInHint", "to start a chat with our team.")}
        </div>
      )}

      {/* FAQ */}
      <div className="mt-6" data-testid="support-faq">
        <div className="text-[10px] uppercase tracking-widest font-black text-ink-textDim">// FAQ</div>
        <h2 className="text-lg font-black mt-1">{t("support.faqTitle", "Frequently Asked Questions")}</h2>
        <div className="mt-3 bg-ink-surface border border-ink-border rounded-2xl divide-y divide-ink-border">
          {FAQ.map((f, i) => {
            const open = openFaqIdx === i;
            return (
              <div key={i} data-testid={`support-faq-item-${i}`}>
                <button
                  onClick={() => setOpenFaqIdx(open ? -1 : i)}
                  className="w-full flex items-center justify-between px-4 py-3 text-start hover:bg-ink-surface2 transition"
                  data-testid={`support-faq-q-${i}`}
                >
                  <span className="font-bold text-sm pr-3">{f.q}</span>
                  <ChevronDown size={16} className={`shrink-0 transition ${open ? "rotate-180 text-brand-accent" : "text-ink-textDim"}`} />
                </button>
                {open && (
                  <div className="px-4 pb-4 text-sm text-ink-textDim leading-relaxed" data-testid={`support-faq-a-${i}`}>
                    {f.a}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div className="mt-8 text-[10px] uppercase font-black text-ink-textFaint text-center">
        {t("support.footer", "We usually reply within a few hours.")}
      </div>
    </div>
  );
}

function SupportStateBadge({ state, agentName }) {
  const { t } = useTranslation();
  const map = {
    bot: { label: t("support.stateBot", "Bot"), cls: "bg-electric-teal/15 border-electric-teal/40 text-electric-teal" },
    verified: { label: t("support.stateBot", "Bot"), cls: "bg-electric-teal/15 border-electric-teal/40 text-electric-teal" },
    waiting_agent: { label: t("support.stateWaiting", "Waiting for agent…"), cls: "bg-amber-gold/15 border-amber-gold/40 text-amber-gold animate-pulse" },
    with_agent: { label: agentName ? t("support.stateWithAgent", `${agentName} · online`) : t("support.stateAgentOnline", "Agent online"), cls: "bg-brand-accent/15 border-brand-accent/40 text-brand-accent" },
    closed: { label: t("support.stateClosed", "Session closed"), cls: "bg-white/5 border-white/10 text-white/50" },
  };
  const m = map[state] || map.bot;
  return (
    <div className={`text-[9px] font-black uppercase tracking-widest rounded-full px-2 py-0.5 border ${m.cls}`} data-testid={`support-state-${state}`}>
      {m.label}
    </div>
  );
}
